import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { joinWaitingList } from "@/lib/api";

export default function WaitingListForm({ open, onClose, pkg }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", whatsapp: "", message: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (!open || !pkg) return null;

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await joinWaitingList({ ...form, package_slug: pkg.slug });
      toast.success(`You're on the ${pkg.title} waiting list. We'll reach out if a spot opens.`);
      setForm({ name: "", email: "", phone: "", whatsapp: "", message: "" });
      onClose();
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-md overflow-y-auto"
        onClick={onClose} data-testid="waitlist-modal">
        <div className="min-h-full flex items-center justify-center p-4 py-12">
          <motion.form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl" data-testid="waitlist-form">
            <div className="h-2 bg-gradient-to-r from-[#c8102e] via-[#c9a24b] to-[#4b2e83]" />
            <div className="p-6 md:p-8">
              <button type="button" onClick={onClose} className="float-right text-black/50 hover:text-black" data-testid="waitlist-close">✕</button>
              <div className="text-[10px] tracking-[0.28em] uppercase text-black/60">Waiting list · {pkg.city}</div>
              <h3 className="mt-2 font-display text-2xl md:text-3xl text-black">{pkg.title}</h3>
              <p className="text-black/60 text-sm mt-2">This package is fully allocated. Join the waiting list — if a spot opens, you'll be first to hear.</p>

              <div className="mt-5 space-y-4">
                <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Name</span>
                  <input required value={form.name} onChange={upd("name")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="wl-name"/>
                </label>
                <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Email</span>
                  <input required type="email" value={form.email} onChange={upd("email")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="wl-email"/>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Phone</span>
                    <input required value={form.phone} onChange={upd("phone")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="wl-phone"/>
                  </label>
                  <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">WhatsApp</span>
                    <input value={form.whatsapp} onChange={upd("whatsapp")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black" data-testid="wl-whatsapp"/>
                  </label>
                </div>
                <label className="block"><span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Anything else?</span>
                  <textarea value={form.message} onChange={upd("message")} className="w-full mt-1 border-b border-black/20 py-2 bg-transparent focus:outline-none focus:border-black min-h-[60px]" data-testid="wl-message"/>
                </label>
              </div>

              <button type="submit" disabled={loading} className="mt-6 w-full rounded-full py-3.5 font-bold tracking-[0.22em] uppercase text-[12px] bg-black text-white" data-testid="wl-submit">
                {loading ? "Adding…" : "Join the Waiting List"}
              </button>
            </div>
          </motion.form>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
